import { useRouter } from "@tanstack/react-router";
import { startTransition, useEffect, useRef } from "react";
import { apiClient } from "@/api/endpoints";
import { useAuth } from "./AuthContext";
import { getAuthErrorMessage } from "./errors";

export const useSetupAuthentication = () => {
  const auth = useAuth();
  const router = useRouter();
  const isAuthenticatingRef = useRef(false);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const navigateToAvtaler = () => {
      const currentPath = window.location.pathname;
      const isAlreadyOnAvtaler = currentPath.includes("/avtaler");

      // Don't navigate if already on /avtaler or if we've already navigated
      if (isAlreadyOnAvtaler || hasNavigatedRef.current) {
        return;
      }

      hasNavigatedRef.current = true;

      // startTransition ensures React processes the state update before navigation,
      // preventing the beforeLoad guard from seeing stale auth context
      startTransition(() => {
        router.navigate({ to: "/avtaler", replace: true }).catch((error) => {
          if (error && typeof error === "object" && "message" in error) {
            console.error("Navigation to /avtaler failed:", error);
          }
        });
      });
    };
    const user = auth.oidcUser;
    if (!user?.id_token) {
      return;
    }

    if (auth.isAuthenticated) {
      navigateToAvtaler();
      return;
    }

    // Guard: prevent concurrent authentication calls
    if (isAuthenticatingRef.current || !user.access_token) {
      return;
    }

    isAuthenticatingRef.current = true;

    const authenticate = async () => {
      try {
        await apiClient.auth.signin(user.access_token);
        const accountInfo = await apiClient.auth.getUserInfo();
        await auth.setupAccount(accountInfo);
      } catch (error) {
        console.error("Error during authentication setup:", error);
        const errorParams = getAuthErrorMessage(error);
        await router.navigate({
          to: "/",
          search: errorParams,
        });
      } finally {
        isAuthenticatingRef.current = false;
      }
    };

    authenticate();
  }, [auth.oidcUser, auth.isAuthenticated, auth.setupAccount, router]);
};
